import { Inject, Injectable } from "@angular/core";
import { LIBRARY } from "@app/evaluation/library";
import { SessionStorage } from "@core/browser";
import { CustomProtocolService, FilterBuilder, OrganizationProtocolPreference, ProtocolFilter, Status, translateCommonErrorStatus } from "@core/data";
import { OrganizationProtocolPreferencesService } from "@core/data/api/organization-protocol-preferences.service";
import { Snackbar } from "@core/material";
import { Library, Protocol, ProtocolDictionary } from "@library";
import { Action, Selector, State, StateContext } from "@ngxs/store";
import { tap } from "rxjs/operators";

export interface ProtocolManagerStateModel {
    status: Status;
    data: ProtocolDictionary;
    orgKey: string;
    protocolOptions: Protocol[];
    preferences: OrganizationProtocolPreference[];
    filter: ProtocolFilter;
}

const DEFAULTS: ProtocolManagerStateModel = {
    status: Status.UNINITIALIZED,
    data: null,
    orgKey: null,
    protocolOptions: [],
    preferences: [],
    filter: {
        showFavourites: true,
        showHidden: false,
        crops: [],
        sampleTypes: [],
        baseProtocols: true,
        includeCustomProtocols: true,
        currentId: null,
    },
}

export class InitProtocolManagerState {
    static readonly type = '[ProtocolManagerState] Init';
    constructor( public orgKey: string, public withPreferences: boolean = false, public filter: Partial<ProtocolFilter> = DEFAULTS.filter ){}
}

export class LoadOrganisationPreference {
    static readonly type = '[ProtocolManagerState] Load Preference';
    constructor( public filter: Partial<ProtocolFilter> = null ) {}
}

@State<ProtocolManagerStateModel>({
    name: 'protocol_manager_state',
    defaults: DEFAULTS,
})
@Injectable()
export class ProtocolManagerState {

    @Selector()
    static data(state: ProtocolManagerStateModel){
        return state.data;
    }

    @Selector()
    static protocolOptions(state: ProtocolManagerStateModel){
        return state.protocolOptions;
    }

    @Selector()
    static filter(state: ProtocolManagerStateModel){
        return state.filter;
    }

    constructor(
        private _snackbar: Snackbar,
        private _protocol_preferencesService: OrganizationProtocolPreferencesService,
        private _custom_protocolService: CustomProtocolService,
        @Inject(LIBRARY) private _library: Library,
        @Inject(SessionStorage) private _session: SessionStorage,
    ){}

    @Action(InitProtocolManagerState, {cancelUncompleted: true})
    initProtocolManagerState(ctx: StateContext<ProtocolManagerStateModel>, action: InitProtocolManagerState){
        this._session.set('prev_org_key', null)
        this._session.set('auth_org_key', action.orgKey)

        this._custom_protocolService.allProtocols();

        const status = action.withPreferences ? Status.LOADING : Status.OK;

        const protocolFilter: ProtocolFilter = {
            ...DEFAULTS.filter,
            ...action.filter
        }

        ctx.setState({
            ...DEFAULTS,
            status,
            orgKey : action.orgKey,
            filter: protocolFilter,
        })

        if (action.withPreferences) ctx.dispatch(new LoadOrganisationPreference);
    }

@Action(LoadOrganisationPreference)
    loadOrganisationPreference(ctx: StateContext<ProtocolManagerStateModel>, action: LoadOrganisationPreference) {
        const state = ctx.getState();

        let protocolFilter: ProtocolFilter = {
            ...state.filter,
        }

        if (action.filter) {
            protocolFilter = {
                ...protocolFilter,
                ...action.filter,
            }
        }

        ctx.patchState({
            status: Status.LOADING,
            filter: protocolFilter,
        })

        const filter = (new FilterBuilder());

        filter.setQuery('ownerOrgKey', state.orgKey);

        return this._protocol_preferencesService.query(filter.get())
            .pipe(
                tap(
                    result => {
                        ctx.patchState({
                            preferences: result.data,
                        })

                        this.loadProtocolOptions(ctx, state.orgKey);
                    },
                    error => {
                        ctx.patchState({
                            preferences: state.preferences ? state.preferences : [],
                            status: translateCommonErrorStatus(error)
                        });
                    }
                )
            )
    }

    private loadProtocolOptions(ctx: StateContext<ProtocolManagerStateModel>, orgKey: string) {
        const state = ctx.getState();

        this._custom_protocolService.allProtocolsObserved(orgKey, state.preferences, state.filter).subscribe(all => {
            if (!all || all.length == 0) this._snackbar.error("No protocols found, contact your Organisation's Administrators to update your protocol preferences.")

            ctx.patchState({
                status: Status.OK,
                protocolOptions: all,
            })
        });
    }
}